import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faTrash,
  faComments,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { ContextMenu } from "primereact/contextmenu";
import { useTranslation } from "react-i18next";
import { useChatBotStore } from "../../store/ChatBotStore";
import moment from "moment";

const ChatBotSidebar = () => {
  const { t } = useTranslation("translations");
  const {
    sidebarOpen,
    toggleSidebar,
    conversations,
    activeConversationId,
    createNewConversation,
    selectConversation,
    deleteConversation,
    deletingConversation,
    loadingConversations,
    loadingMoreConversations,
    hasMoreConversations,
    loadMoreConversations,
  } = useChatBotStore();

  const contextMenuRef = useRef(null);
  const conversationsListRef = useRef(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const contextMenuItems = [
    {
      label: t("chatbot.delete"),
      icon: "pi pi-trash",
      className: "context-menu-delete",
      command: () => {
        if (selectedConversation) {
          deleteConversation(selectedConversation.id);
          setSelectedConversation(null);
        }
      },
    },
  ];

  const formatTimestamp = (timestamp) => {
    const date = moment(timestamp);
    const now = moment();
    const diffDays = now.diff(date, "days");

    if (diffDays === 0) {
      return date.format("HH:mm");
    } else if (diffDays === 1) {
      return t("chatbot.yesterday");
    } else if (diffDays < 7) {
      return date.format("ddd");
    } else {
      return date.format("MMM D");
    }
  };

  const handleContextMenu = (e, conversation) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedConversation(conversation);
    if (contextMenuRef.current) {
      contextMenuRef.current.show(e.nativeEvent || e);
    }
  };

  const handleDeleteConversation = (e, conversationId) => {
    e.stopPropagation();
    if (deletingConversation) return;
    deleteConversation(conversationId);
  };

  const handleScroll = (e) => {
    const element = e.target;
    const scrollThreshold = 50;

    if (
      element.scrollHeight - element.scrollTop - element.clientHeight < scrollThreshold &&
      hasMoreConversations &&
      !loadingMoreConversations
    ) {
      loadMoreConversations();
    }
  };

  useEffect(() => {
    const listElement = conversationsListRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => {
        listElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [hasMoreConversations, loadingMoreConversations, loadMoreConversations]);

  return (
    <div className={`chatbot-sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
      <ContextMenu
        ref={contextMenuRef}
        model={contextMenuItems}
        className="chatbot-context-menu"
        appendTo={document.body}
      />

      <div className="sidebar-header">
        {sidebarOpen && (
          <>
            <h3>{t("chatbot.conversations")}</h3>
            <button
              className="new-chat-btn"
              onClick={createNewConversation}
              title={t("chatbot.newConversation")}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </>
        )}
        <button className="toggle-btn" onClick={toggleSidebar}>
          <FontAwesomeIcon icon={sidebarOpen ? faChevronLeft : faChevronRight} />
        </button>
      </div>

      {sidebarOpen && (
        <div className="conversations-list" ref={conversationsListRef}>
          {loadingConversations ? (
            <>
              {[1, 2, 3, 4, 5,6,7,8,9,10].map((i) => (
                <div key={i} className="conversation-item-shimmer">
                  <div className="conversation-info-shimmer">
                    <div className="shimmer-line shimmer-title"></div>
                    <div className="shimmer-line shimmer-preview"></div>
                  </div>
                  <div className="conversation-meta-shimmer">
                    <div className="shimmer-line shimmer-time"></div>
                  </div>
                </div>
              ))}
            </>
          ) : conversations.length === 0 ? (
            <div className="no-conversations">
              <FontAwesomeIcon icon={faComments} />
              <p>{t("chatbot.noConversationsYet")}</p>
              <button onClick={createNewConversation}>{t("chatbot.startNewChat")}</button>
            </div>
          ) : (
            <>
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`conversation-item ${
                    activeConversationId === conversation.id ? "active" : ""
                  } ${selectedConversation?.id === conversation.id ? "context-selected" : ""}`}
                  onClick={() => selectConversation(conversation.id)}
                  onContextMenu={(e) => handleContextMenu(e, conversation)}
                >
                  <div className="conversation-info">
                    <span className="conversation-title">{conversation.title}</span>
                    <span className="conversation-preview">
                      {conversation.lastMessage || t("chatbot.noMessages")}
                    </span>
                  </div>
                  <div className="conversation-meta">
                    <span className="conversation-time">
                      {formatTimestamp(conversation.timestamp)}
                    </span>
                    <button
                      className="delete-btn delete-btn--visible"
                      onClick={(e) => handleDeleteConversation(e, conversation.id)}
                      title={t("chatbot.deleteConversation")}
                      disabled={deletingConversation}
                    >
                      <FontAwesomeIcon icon={deletingConversation ? faSpinner : faTrash} spin={deletingConversation} />
                    </button>
                  </div>
                </div>
              ))}
              {loadingMoreConversations && (
                <div className="loading-more-conversations">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t("chatbot.loadingMore")}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!sidebarOpen && (
        <div className="collapsed-actions">
          <button
            className="collapsed-btn"
            onClick={createNewConversation}
            title={t("chatbot.newConversation")}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatBotSidebar;
